import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { LoginPage } from './pages/auth/LoginPage';
import { MainLayout } from './components/layout/MainLayout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { DashboardPage } from './pages/dashboard/DashboardPage';
import { VehiclesPage } from './pages/fleet/VehiclesPage';
import { VehicleDetailsPage } from './pages/fleet/VehicleDetailsPage';
import { DriversPage } from './pages/fleet/DriversPage';
import { RoutesPage } from './pages/fleet/RoutesPage';
import { TripsPage } from './pages/trips/TripsPage';
import { CreateTripPage } from './pages/trips/CreateTripPage';
import { TripDetailsPage } from './pages/trips/TripDetailsPage';
import { FuelRecordsPage } from './pages/fuel/FuelRecordsPage';
import { AllowancesPage } from './pages/finance/AllowancesPage';
import { InvoicesPage } from './pages/finance/InvoicesPage';
import { ProFormaInvoicesPage } from './pages/finance/ProFormaInvoicesPage';
import { PaymentsPage } from './pages/finance/PaymentsPage';
import { ReportsPage } from './pages/reports/ReportsPage';
import { FinanceReportsPage } from './pages/reports/FinanceReportsPage';
import { VehicleROIDashboard } from './pages/vehicles/VehicleROIDashboard';
import { ExpenseManagement } from './pages/vehicles/ExpenseManagement';
import { ContractsPage } from './pages/contracts/ContractsPage';
import { CreateContractPage } from './pages/contracts/CreateContractPage';
import { UsersPage } from './pages/admin/UsersPage';
import { ProfilePage } from './pages/profile/ProfilePage';
import { PayrollPage } from './pages/payroll/PayrollPage';
import { UserRole } from './types';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/dashboard" replace />} />
            <Route path="dashboard" element={<DashboardPage />} />
            
            <Route
              path="fleet/vehicles"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR]}>
                  <VehiclesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="vehicles/:id"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR]}>
                  <VehicleDetailsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fleet/drivers"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR]}>
                  <DriversPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="fleet/routes"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.AUDITOR]}>
                  <RoutesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="vehicles/roi"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER]}>
                  <VehicleROIDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="vehicles/expenses"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER]}>
                  <ExpenseManagement />
                </ProtectedRoute>
              }
            />
            
            <Route path="trips" element={<TripsPage />} />
            <Route
              path="trips/create"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER]}>
                  <CreateTripPage />
                </ProtectedRoute>
              }
            />
            <Route path="trips/:id" element={<TripDetailsPage />} />

            <Route
              path="contracts"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER, UserRole.FINANCE_OFFICER]}>
                  <ContractsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="contracts/new"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FLEET_MANAGER]}>
                  <CreateContractPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="fuel"
              element={
                <ProtectedRoute
                  requiredRoles={[
                    UserRole.ADMIN,
                    UserRole.FLEET_MANAGER,
                    UserRole.FINANCE_OFFICER,
                    UserRole.AUDITOR,
                    UserRole.DRIVER,
                  ]}
                >
                  <FuelRecordsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/allowances"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER, UserRole.AUDITOR]}>
                  <AllowancesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/invoices"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER, UserRole.AUDITOR]}>
                  <InvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/proforma-invoices"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER]}>
                  <ProFormaInvoicesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/payments"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER]}>
                  <PaymentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="reports"
              element={
                <ProtectedRoute
                  requiredRoles={[
                    UserRole.ADMIN,
                    UserRole.FLEET_MANAGER,
                    UserRole.FINANCE_OFFICER,
                    UserRole.AUDITOR,
                  ]}
                >
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="finance/reports"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER]}>
                  <FinanceReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="admin/users"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="payroll"
              element={
                <ProtectedRoute requiredRoles={[UserRole.ADMIN, UserRole.FINANCE_OFFICER]}>
                  <PayrollPage />
                </ProtectedRoute>
              }
            />
            
            <Route path="profile" element={<ProfilePage />} />
            
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#363636',
            color: '#fff',
          },
          success: {
            duration: 3000,
            iconTheme: {
              primary: '#43A047',
              secondary: '#fff',
            },
          },
          error: {
            duration: 4000,
            iconTheme: {
              primary: '#D32F2F',
              secondary: '#fff',
            },
          },
        }}
      />
    </QueryClientProvider>
  );
}

export default App;
